// The social and world windows (NPC dialogue, quest journal and tracker, party frame, invite prompts, right-click menus,
// friend and ignore lists, admin panel, world map and the settings panels).
import { app } from '../app';
import { api, content, itemsById, mobsById } from '../api';
import { Widget, focus, type UiPointerEvent, Checkbox, widget, fill, uiScale, rowIcon, fontSize, itemIcon, characterIcon, moneyView, setUiScale, autoUiScale, TEXT_SIZES, TEXT_DENSITIES, setTextDensity } from '../ui/compose';
import { hoverTip, itemTip, simpleTip } from '../ui/tooltips';
import { settings, saveSettings } from '../settings';
import { AdminAction } from '../../../shared/src/constants';
import { button, closeButton, titleBar, slotEl } from './items';
import type { MapChunks } from './minimap';
import type { Windows } from './windows';

const READY = '#96dc5a', LOST = '#f09646', GOLD = '#ffd246', SECTION = '#d6c496';

interface Objective { text: string; have: number; need: number; count: boolean; lost: number }

/** A checkbox, the objective and its progress. */
function objectiveRow(o: Objective, started: boolean): Widget {
  const done = o.have >= o.need;
  const lost = started && o.lost > 0 && !done;
  // A framed square: an edge-coloured box with the fill inset by a pixel.
  const edge = done && started ? READY : '#a19a90';
  const box = widget('box', { style: { width: '0.7em', height: '0.7em', flex: 'none', padding: '1px', background: edge } },
    widget('box', { style: { flex: '1', background: done && started ? READY : '#141217' } }));
  const color = !started ? 'var(--text)' : lost ? LOST : done ? READY : 'var(--text)';
  const count = !o.count ? '' : !started ? `x${o.need}` : lost ? `-${o.lost}  ${o.have}/${o.need}` : `${o.have}/${o.need}`;
  return widget('box', { class: 'row objective', style: { gap: `3px` } }, box, widget('text', { class: 'grow', style: { color } }, o.text),
    count ? widget('text', { style: { color: lost ? LOST : started && done ? READY : 'var(--dim)' } }, count) : null);
}

/** A quest's rewards: copper, then items (with their tooltips). */
function rewardRows(w: Windows, q: { copper: number; rewards: { item: number; count: number }[] }): Widget[] {
  const rows: Widget[] = [];
  if (q.copper > 0) rows.push(moneyView(q.copper));
  for (const r of q.rewards) {
    const def = itemsById.get(r.item);
    const row = widget('box', { class: 'row' }, itemIcon(r.item, rowIcon()), widget('text', { style: { color: def?.qualityColor } }, r.count > 1 ? `${def?.name} x${r.count}` : def?.name ?? '?'));
    hoverTip(row, () => itemTip(r.item, { count: r.count, hint: 'Quest reward', compare: true, shift: w.game.shiftHeld }));
    rows.push(row);
  }
  return rows;
}

// =============================================================================================================
// NPC dialogue
// =============================================================================================================

interface DialogueData {
  gone: boolean; name: string; appearance: number; greeting: string; outfit: number[]; answered: boolean;
  quests: { id: number; title: string; status: string; description: string; objectives: Objective[]; copper: number; exp: number; rewards: { item: number; count: number }[] }[];
}

export class DialogueWindow {
  readonly el = widget('box', { testId: 'npc-dialogue', class: 'panel window', style: { width: `300px`, left: '50%', top: '50%', transform: 'translate(-50%, -55%)' } });
  npc = 0;

  constructor(private readonly w: Windows) {
    this.el.addEventListener('mousedown', e => e.stopPropagation());
  }

  get isOpen(): boolean { return this.npc !== 0; }
  open(npc: number): void { this.npc = npc; }
  close(): void { this.npc = 0; }

  render(): boolean {
    const d = api.dialogue(this.npc) as DialogueData;
    if (d.gone) return false;
    this.el.style.display = d.answered ? '' : 'none'; // until the server answers there's nothing to show
    const open = d.quests.filter(q => q.status !== 'completed');
    const card = (q: DialogueData['quests'][0]) => {
      const [badge, badgeColor] = q.status === 'offerable' ? ['NEW', GOLD] : q.status === 'completable' ? ['READY', READY] : ['IN PROGRESS', 'var(--dim)'];
      const started = q.status !== 'offerable';
      const rewards = rewardRows(this.w, q);
      const action = q.status === 'offerable' ? 0 : q.status === 'completable' ? 1 : -1;
      return widget('box', { class: 'card col' },
        widget('box', { class: 'row' }, widget('text', { class: 'grow', style: { color: q.status === 'completable' ? READY : 'var(--accent)' } }, q.title),
          widget('text', { class: 'small', style: { color: badgeColor, background: 'rgba(255,255,255,0.08)', padding: `1px 3px` } }, badge)),
        q.status === 'offerable' ? widget('box', { class: 'desc' }, q.description) : null,
        widget('box', { class: 'small', style: { color: SECTION } }, 'OBJECTIVES'), ...q.objectives.map(o => objectiveRow(o, started)),
        rewards.length ? widget('box', { class: 'small', style: { color: SECTION } }, 'REWARDS') : null, ...rewards,
        action >= 0 ? widget('box', { class: 'row' }, widget('text', { class: 'grow' }),
          button(action === 0 ? 'Accept quest' : 'Complete quest', () => api.questAction(q.id, action)))
          : widget('box', { class: 'dim' }, 'Come back when it\'s done.'));
    };
    fill(this.el, 
      widget('box', { class: 'row', style: { alignItems: 'flex-start' } },
        widget('box', { style: { background: 'rgba(0,0,0,0.3)', padding: `2px` } }, characterIcon(d.appearance, 24, 0, 0, d.outfit)),
        widget('box', { class: 'col grow' }, titleBar(d.name, 'E', () => this.w.closeStation()), widget('box', { class: 'desc' }, `"${d.greeting}"`))),
      open.length ? null : widget('box', { class: 'dim' }, 'I\'ve nothing more for you today.'),
      ...open.map(card));
    return true;
  }
}

// =============================================================================================================
// quest journal and tracker
// =============================================================================================================

interface ActiveQuest {
  id: number; title: string; ready: boolean; giver: string; description: string; objectives: Objective[];
  copper: number; exp: number; rewards: { item: number; count: number }[];
}

/** J: the quests under way listed on the left, the selected one (the first by default) on the right. Its eye shows or
 *  hides the tracker. Completed quests aren't listed. */
export class QuestJournal {
  // A little above centre, clear of the player frame in the bottom left.
  readonly el = widget('box', { testId: 'quest-journal', class: 'panel window', style: { width: '380px', left: '50%', top: '50%', transform: 'translate(-50%, -55%)' } });
  isOpen = false;
  private selected = 0;

  constructor(private readonly w: Windows) {
    this.el.addEventListener('mousedown', e => e.stopPropagation());
    this.el.style.display = 'none';
  }

  toggle(): void { if (this.isOpen) this.close(); else this.open(); }
  /** Opens it, on a quest if given. */
  open(quest = 0): void { this.isOpen = true; if (quest) this.selected = quest; }
  close(): void { this.isOpen = false; this.el.style.display = 'none'; }

  render(): void {
    this.el.style.display = this.isOpen ? '' : 'none';
    if (!this.isOpen) return;
    const quests = api.quests() as ActiveQuest[];
    const q = quests.find(q => q.id === this.selected) ?? quests[0];
    this.selected = q?.id ?? 0;
    const tracking = settings.questTracker;
    const eye = widget('button', { class: 'button close', testId: 'quest-tracker-toggle', title: tracking ? 'Hide the quest tracker' : 'Show the quest tracker',
      onclick: (e: UiPointerEvent) => { e.stopPropagation(); settings.questTracker = !settings.questTracker; saveSettings(); this.w.refresh(); } },
      widget('box', { class: `close-icon${tracking ? '' : ' ghost'}`, style: { backgroundImage: 'var(--img-eye)' } }));
    const list = widget('box', { class: 'well col scroll', style: { width: '120px', minWidth: '120px', gap: '1px' } },
      ...(quests.length ? quests.map(x => widget('box', { class: `list-row${x.id === this.selected ? ' selected' : ''}`, testId: `journal-quest-${x.id}`,
        onclick: () => { this.selected = x.id; this.w.refresh(); } },
        widget('text', { class: 'grow', style: { color: x.ready ? READY : 'var(--accent)' } }, x.title)))
        : [widget('box', { class: 'small dim' }, 'No active quests.')]));
    // Fixed widths: the window's 380 less its padding (2 x 8) and the gap, so text wraps inside the panes.
    const detail = widget('box', { class: 'well col scroll', style: { width: '240px', minWidth: '240px', gap: '3px' } },
      ...(q ? [
        widget('box', { class: 'tip-title', style: { color: q.ready ? READY : 'var(--accent)' } }, q.title),
        widget('box', { class: 'small', style: { color: q.ready ? READY : 'var(--dim)' } }, q.ready ? `Ready: return to ${q.giver}` : `From ${q.giver}`),
        widget('box', { class: 'desc' }, q.description),
        widget('box', { class: 'small', style: { color: SECTION } }, 'OBJECTIVES'), ...q.objectives.map(o => objectiveRow(o, true)),
        ...(() => { const rewards = rewardRows(this.w, q); return rewards.length ? [widget('box', { class: 'small', style: { color: SECTION } }, 'REWARDS'), ...rewards] : []; })(),
        widget('box', { class: 'row' }, widget('text', { class: 'grow' }),
          widget('button', { class: 'button', onclick: (e: UiPointerEvent) => { e.stopPropagation(); this.w.abandonQuest(q.id, q.title, e.clientX, e.clientY); } }, 'Abandon')),
      ] : [widget('box', { class: 'small dim' }, 'Quests you accept appear here.')]));
    fill(this.el,
      widget('box', { class: 'window-title' }, widget('box', { class: 'icon16 title-icon', style: { backgroundImage: 'var(--img-quests)' } }),
        widget('text', { style: { color: 'var(--title)' } }, 'Quest Journal'),
        widget('text', { class: 'hotkey' }, 'J'), eye, closeButton(() => this.close())),
      widget('box', { class: 'row', style: { alignItems: 'stretch', height: '180px', gap: '4px' } }, list, detail));
  }
}

export class QuestTracker {
  readonly el = widget('box', { class: 'panel quest-tracker' });

  constructor(private readonly w: Windows) { }

  render(): void {
    const quests = api.quests() as ActiveQuest[];
    this.el.style.display = settings.questTracker && quests.length && !this.w.map.isOpen ? '' : 'none';
    fill(this.el, widget('box', { class: 'row tracker-head' }, widget('box', { class: 'icon16', style: { backgroundImage: 'var(--img-diamond)', width: '1em', height: '1em' } }),
      widget('text', { class: 'grow' }, 'Active Quests'), widget('text', { class: 'small dim', style: { fontWeight: '400' } }, 'J')),
      ...quests.map(q => widget('box', { class: 'col', style: { gap: `1px` } },
        widget('box', { class: 'quest-title', style: { color: q.ready ? READY : 'var(--accent)' },
          onclick: () => { this.w.journal.open(q.id); this.w.refresh(); },
          oncontextmenu: (e: UiPointerEvent) => { e.preventDefault(); this.w.questMenu(q.id, q.title, e.clientX, e.clientY); } }, q.title),
        ...(q.ready ? [widget('box', { class: 'row' }, widget('text', { style: { color: GOLD } }, '?'), widget('text', { style: { color: READY } }, `Return to ${q.giver}`))]
          : q.objectives.map(o => objectiveRow(o, true))))));
  }
}

// =============================================================================================================
// party frame and invites
// =============================================================================================================

export interface SocialData {
  server: string; max: number; me: string; players: { name: string; zone: string; appearance: number; party: boolean }[]; ignored: string[];
  friends: { name: string; online: boolean; zone: string; level: number }[];
  leader: string; partyMax: number; isLeader: boolean; party: { name: string; appearance: number; hp: number; zone: string; online: boolean; id: number }[];
  invite?: { from: string; left: number }; tradeInvite?: { from: string; left: number };
}

export class PartyFrame {
  readonly el = widget('box', { class: 'panel party-frame' });

  constructor(private readonly w: Windows) { }

  render(s: SocialData, myHp: number, myZone: string): void {
    this.el.style.display = s.party.length ? '' : 'none';
    if (!s.party.length) return;
    fill(this.el, widget('box', { class: 'row', style: { color: 'var(--accent)', fontWeight: '500' } },
      widget('text', { class: 'grow' }, 'Party'), widget('text', { class: 'small dim' }, `${s.party.length}/${s.partyMax}`)), widget('box', { class: 'rule' }), ...s.party.map(m => {
      const isMe = m.name.toLowerCase() === s.me.toLowerCase(), leader = m.name.toLowerCase() === s.leader.toLowerCase();
      const face = widget('box', { class: 'well party-face' }, characterIcon(m.appearance, 16));
      if (!m.online) face.style.opacity = '0.45';
      const hp = isMe ? myHp : m.hp / 255;
      return widget('box', { class: 'row party-row', oncontextmenu: (e: UiPointerEvent) => { e.preventDefault(); this.w.partyMenu(m, s, e.clientX, e.clientY); } },
        face, widget('box', { class: 'col grow', style: { gap: `2px` } },
          widget('box', { class: 'row', style: { gap: `2px` } }, widget('text', { style: { color: isMe ? '#ffd678' : m.online ? 'var(--text)' : 'var(--dim)', whiteSpace: 'nowrap', flex: '1', minWidth: '0' } }, m.name),
            leader ? widget('text', { class: 'accent' }, '*') : null),
          m.online ? widget('box', { class: 'bar', style: { width: '100%', height: `4px` } }, widget('box', { class: 'bar-fill', style: { width: `${Math.max(0, Math.min(1, hp)) * 100}%`, background: '#c83434' } }))
            : widget('box', { class: 'small dim' }, 'Offline'),
          m.online && !isMe && m.zone && m.zone !== myZone ? widget('box', { class: 'small dim' }, m.zone) : null));
    }));
  }
}

export class Invites {
  readonly el = widget('box', { class: 'invites' });
  private key = '';

  render(s: SocialData): void {
    const key = JSON.stringify([s.invite?.from, s.tradeInvite?.from, Math.ceil(s.invite?.left ?? 0), Math.ceil(s.tradeInvite?.left ?? 0)]);
    if (key === this.key) return;
    this.key = key;
    const prompt = (text: string, color: string, left: number, yes: () => void, no: () => void) =>
      widget('box', { class: 'panel col', style: { alignItems: 'center', padding: `5px`, minWidth: `170px` } },
        widget('box', { style: { color } }, text),
        widget('box', { class: 'row' }, button('Accept', yes), button('Decline', no), widget('text', { class: 'small dim' }, `${Math.ceil(left)}s`)));
    fill(this.el, 
      s.invite ? prompt(`${s.invite.from} invited you to a party`, '#78d2f0', s.invite.left, () => api.partyAction(1, ''), () => api.partyAction(2, '')) : '',
      s.tradeInvite ? prompt(`${s.tradeInvite.from} wants to trade`, 'var(--accent)', s.tradeInvite.left, () => api.tradeReply(true), () => api.tradeReply(false)) : '');
  }
}

// =============================================================================================================
// right-click menu
// =============================================================================================================

export interface MenuItem { text: string; action: () => void; enabled?: boolean }

export class ContextMenu {
  el: Widget | null = null;

  open(title: string, x: number, y: number, items: MenuItem[]): void {
    this.close();
    const el = widget('box', { class: 'panel menu-popup' }, widget('box', { class: 'menu-head' }, title), ...items.map(i => widget('box', {
      class: `menu-item${i.enabled === false ? ' disabled' : ''}`,
      onmousedown: (e: UiPointerEvent) => { e.stopPropagation(); if (i.enabled === false) return; app.audio.play('sfx_ui_click', 0.5, 0); this.close(); i.action(); },
    }, i.text)));
    app.ui.overlay.append(el);
    el.style.left = `${Math.min(x / uiScale, innerWidth / uiScale - el.offsetWidth - 4)}px`;
    el.style.top = `${Math.min(y / uiScale, innerHeight / uiScale - el.offsetHeight - 4)}px`;
    this.el = el;
    window.addEventListener('pointerdown', this.outside, true);
  }

  /** A press outside closes the menu, and only that (it is not also a world click). */
  private outside = (e: PointerEvent) => {
    const hit = app.ui.hitTest(e.clientX, e.clientY);
    if (hit?.closest('.menu-popup') !== this.el) { this.close(); app.ui.takePress(); }
  };

  get isOpen(): boolean { return this.el !== null; }

  close(): void {
    window.removeEventListener('pointerdown', this.outside, true);
    this.el?.dispose();
    this.el = null;
  }
}

// =============================================================================================================
// friend list
// =============================================================================================================

export class FriendsWindow {
  readonly el = widget('box', { class: 'panel window', style: { width: `200px`, left: '50%', top: '25%', transform: 'translateX(-50%)' } });
  private readonly name = widget('input', { class: 'field', placeholder: 'Character name', maxLength: content.maxNameLength });
  isOpen = false;

  constructor(private readonly w: Windows) {
    this.name.editor.filter = c => /[A-Za-z]/.test(c);
    this.name.addEventListener('keydown', e => {
      e.stopPropagation();
      if (e.key === 'Enter') this.add();
      if (e.key === 'Escape') this.close();
    });
    this.el.addEventListener('mousedown', e => e.stopPropagation());
  }

  toggle(): void { if (this.isOpen) this.close(); else { this.isOpen = true; this.name.value = ''; } }
  close(): void { this.isOpen = false; this.name.blur(); this.el.style.display = 'none'; }

  private add(): void {
    if (this.name.value.trim().length < 3) return;
    api.friend(this.name.value.trim(), true);
    this.name.value = '';
  }

  render(s: SocialData): void {
    this.el.style.display = this.isOpen ? '' : 'none';
    if (!this.isOpen) return;
    const focused = focus.current === this.name;
    const online = s.friends.filter(f => f.online).length;
    fill(this.el, titleBar('Friends', `${online} online, ${s.friends.length}/${content.maxFriends}`, () => this.close(), undefined, 'friends'),
      widget('box', { class: 'row' }, this.name, button('Add', () => this.add())),
      s.friends.length ? null : widget('box', { class: 'small dim' }, 'Nobody yet. Right-click a player or a name in chat to add them.'),
      widget('box', { class: 'col scroll', style: { maxHeight: `140px` } }, ...s.friends.map(f => widget('box', {
        class: 'row friend-row',
        oncontextmenu: (e: UiPointerEvent) => { e.preventDefault(); this.w.friendMenu(f, e.clientX, e.clientY); },
      },
        widget('box', { class: `friend-dot${f.online ? ' online' : ''}` }),
        widget('box', { class: 'col grow', style: { gap: `1px` } },
          widget('box', { class: 'row', style: { gap: `3px` } },
            widget('text', { style: { color: f.online ? 'var(--text)' : 'var(--dim)' } }, f.name),
            f.online ? widget('text', { class: 'small dim' }, `Lv ${f.level}`) : null),
          widget('box', { class: 'small dim' }, f.online ? f.zone : 'Offline')),
        button('Remove', () => api.friend(f.name, false))))));
    if (focused) this.name.focus();
  }
}

// =============================================================================================================
// ignore list
// =============================================================================================================

export class IgnoreWindow {
  readonly el = widget('box', { class: 'panel window', style: { width: `180px`, left: '50%', top: '30%', transform: 'translateX(-50%)' } });
  private readonly name = widget('input', { class: 'field', placeholder: 'Character name', maxLength: content.maxNameLength });
  isOpen = false;

  constructor(private readonly w: Windows) {
    this.name.editor.filter = c => /[A-Za-z]/.test(c);
    this.name.addEventListener('keydown', e => {
      e.stopPropagation();
      if (e.key === 'Enter') this.add();
      if (e.key === 'Escape') this.close();
    });
    this.el.addEventListener('mousedown', e => e.stopPropagation());
  }

  toggle(): void { if (this.isOpen) this.close(); else { this.isOpen = true; this.name.value = ''; } }
  close(): void { this.isOpen = false; this.name.blur(); this.el.style.display = 'none'; }

  private add(): void {
    if (this.name.value.trim().length < 3) return;
    api.ignore(this.name.value.trim(), true);
    this.name.value = '';
  }

  render(s: SocialData): void {
    this.el.style.display = this.isOpen ? '' : 'none';
    if (!this.isOpen) return;
    const focused = focus.current === this.name;
    fill(this.el, titleBar('Ignored players', `${s.ignored.length}/${content.maxIgnored}`, () => this.close()),
      widget('box', { class: 'small dim' }, 'You won\'t see their chat, whispers, invites or trades.'),
      widget('box', { class: 'row' }, this.name, button('Ignore', () => this.add())),
      s.ignored.length ? null : widget('box', { class: 'small dim' }, 'Nobody. Right-click a name in chat to ignore them.'),
      widget('box', { class: 'col scroll', style: { maxHeight: `100px` } }, ...s.ignored.map(n => widget('box', { class: 'row', style: { background: 'rgba(0,0,0,0.25)', padding: `1px 3px` } },
        widget('text', { class: 'grow' }, n), button('Unignore', () => api.ignore(n, false))))));
    if (focused) this.name.focus();
  }
}

// =============================================================================================================
// admin panel (F5)
// =============================================================================================================

interface AdminInfo { me: string; level: number; players: { name: string; zone: string }[]; maps: { key: string; name: string }[] }

export class AdminPanel {
  // Down the right side, clear of the HUD buttons at the bottom; it scrolls when the screen is short. Wide enough for
  // six columns of items.
  readonly el = widget('box', { class: 'panel window scroll', style: { width: `154px`, right: `4px`, top: '4px', bottom: '34px' } });
  isOpen = false;
  placing: 'none' | 'spawn' | 'teleport' = 'none';
  private mob = 0;
  private count = 1;
  private confirmUntil = 0;
  private confirmBelt = false;
  private me = { x: 0, y: 0, f: 0 };
  /** Who the Players section acts on ('' = you), and the level it would set. */
  private target = '';
  private level = 0;
  private renderKey = '';
  god = false;

  constructor(private readonly w: Windows) {
    this.el.addEventListener('mousedown', e => e.stopPropagation());
  }

  toggle(): void { this.isOpen = !this.isOpen; this.placing = 'none'; this.renderKey = ''; this.render(); }
  cancelPlacing(): void { this.placing = 'none'; this.render(); }

  placeAt(x: number, y: number): void {
    const mob = content.mobs[this.mob];
    if (this.placing === 'spawn' && mob) api.admin(0, mob.id, this.count, x, y);
    else if (this.placing === 'teleport') { api.admin(6, 0, 0, x, y); this.placing = 'none'; this.render(); }
  }

  setPlayer(x: number, y: number, f: number, god: boolean): void {
    this.me = { x, y, f };
    if (god !== this.god) { this.god = god; if (this.isOpen) this.render(); }
  }

  /** Redraws when what it lists changed (players coming and going, maps, your level); the windows call it each revision. */
  refresh(): void {
    if (!this.isOpen) return;
    const key = JSON.stringify(api.adminInfo());
    if (key !== this.renderKey) this.render();
  }

  /** A section: its title folds it (remembered), then its contents. */
  private section(title: string, body: () => (Widget | null)[]): (Widget | null)[] {
    const open = !settings.adminCollapsed.includes(title);
    const head = widget('box', { class: 'row admin-section', testId: `admin-${title.toLowerCase().replace(/[^a-z]+/g, '-')}`, onclick: () => {
      settings.adminCollapsed = open ? [...settings.adminCollapsed, title] : settings.adminCollapsed.filter(t => t !== title);
      saveSettings(); this.render();
    } }, widget('text', { class: 'accent' }, open ? '-' : '+'), widget('text', { class: 'grow' }, title));
    return [head, ...(open ? body() : [])];
  }

  render(): void {
    this.el.style.display = this.isOpen ? '' : 'none';
    if (!this.isOpen) return;
    const info = api.adminInfo() as AdminInfo;
    this.renderKey = JSON.stringify(info);
    const mobs = content.mobs, mob = mobs[this.mob];
    const confirming = app.time < this.confirmUntil;
    const clear = (belt: boolean) => {
      if (confirming && this.confirmBelt === belt) { api.admin(2, 0, belt ? 1 : 0, 0, 0); this.confirmUntil = 0; }
      else { this.confirmBelt = belt; this.confirmUntil = app.time + 3; }
      this.render();
    };
    const facing = [[0, 1], [-1, 0], [1, 0], [0, -1]][this.me.f] ?? [0, 1];

    // Players: who, their level, and moving between you.
    const players = info.players;
    let index = players.findIndex(p => p.name.toLowerCase() === (this.target || info.me).toLowerCase());
    if (index < 0) { index = 0; this.target = ''; }
    const chosen = players[index], self = !chosen || chosen.name.toLowerCase() === info.me.toLowerCase();
    if (!this.level) this.level = info.level;
    const pick = (step: number) => {
      if (!players.length) return;
      const next = players[(index + step + players.length) % players.length];
      this.target = next.name.toLowerCase() === info.me.toLowerCase() ? '' : next.name;
      this.render();
    };
    const stepLevel = (d: number) => { this.level = Math.max(1, Math.min(50, this.level + d)); this.render(); };
    const name = self ? '' : chosen.name;

    const shadowSlider = (label: string, key: 'shadowDirection' | 'shadowDistance', max: number, unit: string) => {
      const value = widget('text', { class: 'accent', testId: `${key}-value` }, `${settings[key]}${unit}`);
      const input = widget('input', { type: 'range', min: 0, max, value: String(settings[key]), testId: key });
      input.addEventListener('input', () => {
        settings[key] = Number(input.value); value.textContent = `${input.value}${unit}`; saveSettings();
      });
      return widget('box', {}, widget('box', { class: 'row' }, widget('text', { class: 'grow' }, label), value), input);
    };
    const followSun = new Checkbox({ checked: settings.shadowsFollowSun, testId: 'shadows-follow-sun' });
    followSun.textContent = settings.shadowsFollowSun ? '[x]' : '[ ]';
    followSun.addEventListener('input', () => {
      settings.shadowsFollowSun = followSun.checked;
      followSun.textContent = followSun.checked ? '[x]' : '[ ]'; saveSettings();
    });
    const entityShadows = new Checkbox({ checked: settings.entityShadows, testId: 'entity-shadows' });
    entityShadows.textContent = settings.entityShadows ? '[x]' : '[ ]';
    entityShadows.addEventListener('input', () => {
      settings.entityShadows = entityShadows.checked;
      entityShadows.textContent = entityShadows.checked ? '[x]' : '[ ]'; saveSettings();
    });

    fill(this.el, titleBar('Admin', 'F5', () => this.toggle()),
      ...this.section('Players', () => [
        widget('box', { class: 'row' }, button('<', () => pick(-1)),
          widget('text', { class: 'grow center', title: chosen?.zone ?? '' }, self ? `${info.me} (you)` : chosen.name), button('>', () => pick(1))),
        !self && chosen.zone ? widget('box', { class: 'small dim center' }, chosen.zone) : null,
        !self ? widget('box', { class: 'buttons' }, button('Go to', () => api.admin(11, 0, 0, 0, 0, name)), button('Bring here', () => api.admin(12, 0, 0, 0, 0, name))) : null,
        widget('box', { class: 'row' }, widget('text', { class: 'grow' }, 'Level'),
          button('-10', () => stepLevel(-10)), button('-', () => stepLevel(-1)),
          widget('text', { class: 'accent', style: { minWidth: '16px', textAlign: 'center' } }, String(this.level)),
          button('+', () => stepLevel(1)), button('+10', () => stepLevel(10))),
        button(self ? `Set my level to ${this.level}` : `Set ${chosen.name} to ${this.level}`, () => api.admin(9, 0, this.level, 0, 0, name)),
        widget('box', { class: 'buttons' }, button('Heal me', () => api.admin(3, 0, 0, 0, 0)), button(this.god ? 'God: On' : 'God: Off', () => api.admin(4, 0, 0, 0, 0))),
      ]),
      ...this.section('Travel', () => [
        widget('box', { class: 'buttons' }, button(this.placing === 'teleport' ? 'Click...' : 'Teleport', () => { this.placing = this.placing === 'teleport' ? 'none' : 'teleport'; this.render(); }),
          button('To plaza', () => api.admin(7, 0, 0, 0, 0))),
        ...info.maps.map(m => widget('box', { class: 'row' }, widget('text', { class: 'grow', title: m.key }, m.name), button('Go', () => api.admin(10, 0, 0, 0, 0, m.key)))),
        info.maps.length ? null : widget('box', { class: 'small dim' }, 'No maps listed yet.'),
      ]),
      ...this.section('Spawn', () => [
        mob ? widget('box', { class: 'row' }, button('<', () => { this.mob = (this.mob + mobs.length - 1) % mobs.length; this.render(); }),
          widget('text', { class: 'grow center' }, mob.name), button('>', () => { this.mob = (this.mob + 1) % mobs.length; this.render(); }),
          button('<', () => { this.count = Math.max(1, this.count - 1); this.render(); }), widget('text', { class: 'accent' }, `x${this.count}`),
          button('>', () => { this.count = Math.min(20, this.count + 1); this.render(); })) : null,
        widget('box', { class: 'buttons' },
          button('Near me', () => mob && api.admin(0, mob.id, this.count, this.me.x + facing[0] * 40, this.me.y + facing[1] * 40)),
          button(this.placing === 'spawn' ? 'Placing...' : 'Place...', () => { this.placing = this.placing === 'spawn' ? 'none' : 'spawn'; this.render(); })),
        button('Kill nearby mobs', () => api.admin(5, 0, 0, 0, 0)),
      ]),
      ...this.section('Items', () => [
        widget('box', { class: 'row dim' }, widget('text', { class: 'grow' }, 'Give item'), widget('text', { class: 'small' }, 'shift: stack')),
        widget('box', { class: 'grid', style: { gridTemplateColumns: 'repeat(6, auto)' } }, ...content.items.map(d => {
          const el = slotEl({ item: d.id, count: 1, bound: false });
          el.addEventListener('mousedown', e => { if (e.button === 0) api.admin(1, d.id, e.shiftKey ? d.maxStack : 1, 0, 0); });
          hoverTip(el, () => itemTip(d.id, { hint: 'Click: give 1, shift-click: a stack' }));
          return el;
        })),
        widget('box', { class: 'buttons' }, button(confirming && !this.confirmBelt ? 'Sure?' : 'Clear bag', () => clear(false)),
          button(confirming && this.confirmBelt ? 'Sure?' : 'Clear all', () => clear(true))),
      ]),
      ...this.section('World', () => [
        widget('box', { class: 'small dim' }, 'Time of day (everyone)'),
        ...[[['Dawn', 6 * 60], ['Noon', 12 * 60]], [['Dusk', 18 * 60], ['Night', 22 * 60]]].map(pair => widget('box', { class: 'buttons' },
          ...pair.map(([label, minute]) => button(label as string, () => api.admin(AdminAction.SetTime, minute as number, 0, 0, 0))))),
        widget('box', { class: 'small dim' }, 'Day length (everyone)'),
        ...[[['Pause', 0], ['24 min', 24 * 60]], [['2 min', 120], ['30 s', 30]]].map(pair => widget('box', { class: 'buttons' },
          ...pair.map(([label, seconds]) => button(label as string, () => api.admin(AdminAction.SetDayLength, seconds as number, 0, 0, 0))))),
      ]),
      ...this.section('Shadows (local preview)', () => [
        widget('box', { class: 'row' }, widget('text', { class: 'grow' }, 'Follow time of day'), followSun),
        shadowSlider('Direction', 'shadowDirection', 360, ' deg'),
        shadowSlider('Distance', 'shadowDistance', 300, '%'),
        widget('box', { class: 'row' }, widget('text', { class: 'grow' }, 'Entity shadows'), entityShadows),
        widget('text', { class: 'small dim' }, '0=right, 90=down. Distance: % of height. The sliders apply when not following the time of day.'),
      ]),
      this.placing !== 'none' ? widget('box', { class: 'small accent' }, this.placing === 'teleport' ? 'Click the world to teleport (right-click: cancel)' : `Click to spawn ${mob?.name} x${this.count} (right-click: done)`) : null);
  }
}

// =============================================================================================================
// world map (M)
// =============================================================================================================

export class WorldMap {
  readonly el = widget('box', { class: 'world-map' });
  private readonly canvas = widget('raster', { class: 'sprite' });
  private readonly header = widget('box', { class: 'panel row map-header' });
  private readonly elsewhere = widget('box', { class: 'panel map-elsewhere' });
  isOpen = false;
  private scale = 3;
  private cx = 0;
  private cy = 0;
  private drag: { x: number; y: number; cx: number; cy: number } | null = null;

  constructor(private readonly map: MapChunks) {
    this.el.append(this.canvas, this.header, this.elsewhere);
    this.canvas.addEventListener('wheel', e => {
      e.preventDefault();
      const before = this.toTile(e.clientX, e.clientY);
      this.scale = Math.max(0.5, Math.min(12, this.scale * (e.deltaY < 0 ? 1.25 : 0.8)));
      const after = this.toTile(e.clientX, e.clientY);
      this.cx += before.x - after.x;
      this.cy += before.y - after.y;
      this.draw();
    }, { passive: false });
    this.canvas.addEventListener('mousedown', e => { e.stopPropagation(); this.drag = { x: e.clientX, y: e.clientY, cx: this.cx, cy: this.cy }; });
    window.addEventListener('mousemove', this.move);
    window.addEventListener('mouseup', this.up);
    this.el.style.display = 'none';
  }

  private move = (e: MouseEvent) => {
    if (!this.drag) return;
    this.cx = this.drag.cx - (e.clientX - this.drag.x) / uiScale / this.scale;
    this.cy = this.drag.cy - (e.clientY - this.drag.y) / uiScale / this.scale;
    this.draw();
  };
  private up = () => { this.drag = null; };
  dispose(): void { window.removeEventListener('mousemove', this.move); window.removeEventListener('mouseup', this.up); }

  private toTile(x: number, y: number) { return { x: (x - innerWidth / 2) / uiScale / this.scale + this.cx, y: (y - innerHeight / 2) / uiScale / this.scale + this.cy }; }

  toggle(): void {
    this.isOpen = !this.isOpen;
    this.el.style.display = this.isOpen ? '' : 'none';
    if (this.isOpen) { this.update(); this.recenter(); }
  }

  close(): void { this.isOpen = false; this.el.style.display = 'none'; }

  recenter(): void {
    if (this.map.last) { this.cx = this.map.last.x; this.cy = this.map.last.y; }
    this.draw();
  }

  /** Pulls changed chunks now (the windows refresh the shared chunks every so often anyway). */
  update(): void {
    this.map.refresh();
    this.draw();
  }

  draw(): void {
    if (!this.isOpen || !this.map.last) return;
    const d = this.map.last, c = this.canvas, s = uiScale;
    c.surfaceWidth = innerWidth;
    c.surfaceHeight = innerHeight;
    c.style.width = `${c.surfaceWidth / uiScale}px`; c.style.height = `${c.surfaceHeight / uiScale}px`;
    const g = c.getContext('2d')!;
    g.imageSmoothingEnabled = false;
    g.fillStyle = 'rgba(6,4,6,0.94)';
    g.fillRect(0, 0, c.surfaceWidth, c.surfaceHeight);
    const toScreen = (tx: number, ty: number) => [Math.floor((tx - this.cx) * this.scale * s + c.surfaceWidth / 2), Math.floor((ty - this.cy) * this.scale * s + c.surfaceHeight / 2)];
    for (const [key, chunk] of this.map.chunks) {
      const [x, y] = key.split(',').map(Number);
      const [x0, y0] = toScreen(x * 32, y * 32), [x1, y1] = toScreen((x + 1) * 32, (y + 1) * 32);
      if (x1 < 0 || y1 < 0 || x0 > c.surfaceWidth || y0 > c.surfaceHeight) continue;
      g.drawImage(chunk, x0, y0, x1 - x0, y1 - y0);
    }
    g.textAlign = 'center';
    g.font = `500 ${fontSize}px 'Fira Sans', sans-serif`;
    if (!this.map.chunks.size) { g.fillStyle = '#a0928c'; g.fillText('Nothing explored yet', c.surfaceWidth / 2, c.surfaceHeight / 2); }
    for (const [x, y] of d.entrances) {
      const [px, py] = toScreen(x + 0.5, y + 0.5), r = Math.max(3 * s, this.scale * 1.5);
      g.fillStyle = '#f9923c'; g.fillRect(px - r, py - r, r * 2, r * 2);
      g.fillStyle = '#0c080a'; g.fillRect(px - r + s, py - r + s, r * 2 - 2 * s, r * 2 - 2 * s);
    }
    for (const m of d.party.filter(p => p.here)) {
      const [px, py] = toScreen(m.x, m.y);
      g.fillStyle = '#78d2f0';
      g.beginPath(); g.moveTo(px, py - 4 * s); g.lineTo(px + 4 * s, py); g.lineTo(px, py + 4 * s); g.lineTo(px - 4 * s, py); g.fill();
      g.fillText(m.name, px, py - 7 * s);
    }
    // You: an arrow showing your facing.
    const [mx, my] = toScreen(d.x, d.y), r = 5 * s;
    const dir = [[0, 1], [-1, 0], [1, 0], [0, -1]][d.f];
    g.fillStyle = '#ffd678'; g.strokeStyle = '#000'; g.lineWidth = s;
    g.beginPath();
    g.moveTo(mx + dir[0] * r, my + dir[1] * r);
    g.lineTo(mx - dir[0] * r / 2 + dir[1] * r * 0.8, my - dir[1] * r / 2 + dir[0] * r * 0.8);
    g.lineTo(mx - dir[0] * r / 2 - dir[1] * r * 0.8, my - dir[1] * r / 2 - dir[0] * r * 0.8);
    g.closePath(); g.fill(); g.stroke();
    this.header.replaceChildren(widget('text', { class: 'accent' }, d.zone), widget('text', { class: 'dim' }, `  ${d.tx}, ${d.ty}`), widget('text', { class: 'grow' }), widget('text', { class: 'dim' }, 'M close   wheel zoom   drag pan   C centre'));
    const away = d.party.filter(p => !p.here);
    this.elsewhere.style.display = away.length ? '' : 'none';
    fill(this.elsewhere, widget('box', { style: { color: '#78d2f0' } }, 'Party elsewhere'), ...away.map(p => widget('box', null, `${p.name}: ${p.where}`)));
  }
}

// =============================================================================================================
// settings
// =============================================================================================================

export class SettingsWindow {
  // Anchored by its top, so switching to a shorter tab doesn't move the tabs.
  readonly el = widget('box', { class: 'panel window settings', style: { left: '50%', top: '18%', transform: 'translateX(-50%)', zIndex: '40', width: `220px` } });
  isOpen = false;
  private tab = 'Video';

  constructor() { this.el.addEventListener('mousedown', e => e.stopPropagation()); }

  toggle(): void { this.isOpen = !this.isOpen; this.render(); }
  close(): void { this.isOpen = false; this.render(); }

  render(): void {
    this.el.style.display = this.isOpen ? '' : 'none';
    if (!this.isOpen) return;
    const slider = (label: string, get: () => number, set: (v: number) => void) => {
      const input = widget('input', { type: 'range', min: 0, max: 100, value: String(Math.round(get() * 100)), class: 'grow' });
      const value = widget('text', { style: { minWidth: `22px`, textAlign: 'right' } }, `${Math.round(get() * 100)}%`);
      input.addEventListener('input', () => { set(Number(input.value) / 100); value.textContent = `${input.value}%`; saveSettings(); });
      return widget('box', { class: 'row' }, widget('text', { style: { minWidth: `60px` } }, label), input, value);
    };
    const toggle = (label: string, get: () => boolean, set: (v: boolean) => void) => {
      const check = new Checkbox({ checked: get(), testId: label.toLowerCase().replace(/\s+/g, '-') });
      check.textContent = get() ? '[x]' : '[ ]';
      check.addEventListener('input', () => { set(check.checked); saveSettings(); this.render(); });
      return widget('box', { class: 'row' }, widget('text', { class: 'grow' }, label), check);
    };
    const choice = (label: string, value: string, prev: () => void, next: () => void) =>
      widget('box', { class: 'row' }, widget('text', { class: 'grow' }, label), button('<', () => { prev(); saveSettings(); this.render(); }),
        widget('text', { style: { minWidth: `40px`, textAlign: 'center' } }, value), button('>', () => { next(); saveSettings(); this.render(); }));
    const body: Widget[] = [];
    if (this.tab === 'Video') {
      body.push(choice('UI scale', settings.uiScale === 0 ? 'Auto' : `${settings.uiScale}x`,
        () => { settings.uiScale = Math.max(0, settings.uiScale - 1); setUiScale(settings.uiScale, settings.textSize); },
        () => { settings.uiScale = Math.min(autoUiScale(), settings.uiScale + 1); setUiScale(settings.uiScale, settings.textSize); }),
        choice('Text size', ['Small', 'Normal', 'Large', 'Larger'][settings.textSize] ?? 'Normal',
          () => { settings.textSize = Math.max(0, settings.textSize - 1); setUiScale(settings.uiScale, settings.textSize); },
          () => { settings.textSize = Math.min(TEXT_SIZES.length - 1, settings.textSize + 1); setUiScale(settings.uiScale, settings.textSize); }),
        choice('Text density', `${settings.textDensity}x`,
          () => { settings.textDensity = TEXT_DENSITIES[Math.max(0, TEXT_DENSITIES.indexOf(settings.textDensity) - 1)]; setTextDensity(settings.textDensity); },
          () => { settings.textDensity = TEXT_DENSITIES[Math.min(TEXT_DENSITIES.length - 1, TEXT_DENSITIES.indexOf(settings.textDensity) + 1)]; setTextDensity(settings.textDensity); }),
        widget('text', { class: 'small dim' }, 'Higher text density uses more GPU memory.'),
        choice('Zoom', settings.zoomOffset >= 0 ? `+${settings.zoomOffset}` : String(settings.zoomOffset), () => settings.zoomOffset--, () => settings.zoomOffset++),
        toggle('Shadows', () => settings.shadows, v => settings.shadows = v));
      // Not where the browser can't (iPhone Safari) or the installed app already is full screen.
      if (document.fullscreenEnabled && !matchMedia('(display-mode: fullscreen), (display-mode: standalone)').matches)
        body.push(toggle('Full screen', () => !!document.fullscreenElement, v => { if (v) void document.documentElement.requestFullscreen(); else void document.exitFullscreen(); }));
    } else if (this.tab === 'Audio') {
      body.push(slider('Master', () => settings.masterVolume, v => settings.masterVolume = v),
        slider('Music', () => settings.musicVolume, v => settings.musicVolume = v),
        slider('Effects', () => settings.effectsVolume, v => settings.effectsVolume = v),
        toggle('Mute when unfocused', () => settings.muteWhenUnfocused, v => settings.muteWhenUnfocused = v));
    } else {
      body.push(toggle('Profanity filter', () => settings.profanityFilter, v => settings.profanityFilter = v),
        widget('box', { class: 'small dim' }, 'Masks swear words in other players\' chat.'));
    }
    fill(this.el, titleBar('Settings', '', () => this.close(), undefined, 'settings'),
      widget('box', { class: 'tabs' }, ...['Video', 'Audio', 'Social'].map(t => button(t, () => { this.tab = t; this.render(); }, false, t === this.tab ? 'active' : ''))),
      ...body);
  }
}

export { mobsById, simpleTip };
